/**
 * Fisica restringida.
 *
 * Esta es la pieza donde se decide que el diagrama se lea o no, asi que vale
 * la pena ser explicito:
 *
 *   La coordenada Y de cada persona es su fila, y la fisica NO LA TOCA NUNCA.
 *   No hay fuerza vertical. No existe `vy`. La fila se escribe una vez al
 *   construir y no se vuelve a escribir.
 *
 * En horizontal si hay libertad, y ahi actuan:
 *
 *   - un resorte corto y fuerte entre las dos personas de una pareja
 *   - un resorte suave que mantiene cohesionado a un grupo de hermanos
 *   - un resorte que cuelga a los hijos del punto medio de sus progenitores,
 *     y otro mas flojo en sentido contrario
 *   - una separacion minima entre vecinos de la misma fila, resuelta por
 *     posicion y no por fuerza, que es lo que evita que las tarjetas se
 *     solapen sin que el sistema se ponga a vibrar
 *   - amortiguacion alta, para que todo se asiente sin rebotes
 *
 * La separacion se aplica recorriendo cada fila en el orden que decidio
 * `orden.ts`, asi que ese orden se conserva por construccion y el trabajo de
 * minimizar cruces no se echa a perder.
 */
import type { Indice } from "../core/grafo.js";
import { ALTO_FILA, separacionMinima } from "./medidas.js";

export interface NodoFisica {
  handle: string;
  fila: number;
  /** Posicion horizontal. La mueve la fisica. */
  x: number;
  /** Posicion vertical. Se calcula de la fila y no se toca. */
  readonly y: number;
  /** Velocidad horizontal. No hay vertical, a proposito. */
  vx: number;
  /** Si la persona esta sujeta (arrastrandola, o clavada por quien mira). */
  fijado: boolean;
}

/** Una familia, preparada para que el paso de fisica no tenga que buscar nada. */
interface Nucleo {
  /** Las dos personas de la pareja, si estan las dos y en la misma fila. */
  pareja?: [string, string];
  /** Progenitores presentes, para el punto medio. */
  progenitores: string[];
  /** Hijos presentes. */
  hijos: string[];
}

const K_PAREJA = 0.09;
const K_HERMANOS = 0.018;
/**
 * Resorte entre vecinos de la misma fila. Es el UNICO termino que contrae el
 * conjunto: sin el, el diagrama se ensancha sin tope.
 */
const K_VECINOS = 0.022;
/** Los hijos buscan el punto medio de sus padres. */
const K_HIJOS = 0.055;
/** Y los padres ceden un poco hacia sus hijos, pero menos. */
const K_PADRES = 0.014;
const AMORTIGUACION = 0.8;
/**
 * Cuando se considera asentado.
 *
 * El criterio es RELATIVO al ancho del diagrama, no absoluto. Un movimiento
 * de 0,2 unidades por paso es visible en un arbol de 80 personas y totalmente
 * invisible en uno de 800, donde el lienzo mide diez veces mas. Con un umbral
 * fijo, los arboles grandes seguian simulando mucho despues de estar quietos
 * a efectos practicos.
 */
const ENERGIA_RELATIVA = 1e-5;
/** Y un suelo, para arboles diminutos donde el ancho no dice nada. */
const ENERGIA_MINIMA = 0.02;
/** Tope de velocidad, para que un arrastre brusco no dispare nada. */
const VEL_MAX = 40;

export class Fisica {
  readonly nodos = new Map<string, NodoFisica>();
  /** Fila -> handles en el orden decidido por orden.ts. */
  private readonly ordenFilas: string[][] = [];
  private readonly nucleos: Nucleo[] = [];
  /** Grupos de hermanos, para la cohesion. */
  private readonly gruposHermanos: string[][] = [];

  /** Bloques (parejas pegadas) por fila, en orden. */
  private readonly bloquesPorFila: string[][][] = [];
  /**
   * Separacion minima entre cada par contiguo de cada fila.
   *
   * No es una sola cifra para todos: una pareja va casi tocandose, unos
   * hermanos juntos pero despegados, y dos familias distintas con un hueco
   * ancho. Si se usa una unica separacion, al asentarse queda todo el mundo
   * equidistante y una fila entera parece un grupo de hermanos.
   */
  private readonly separaciones: number[][] = [];
  /** Familias en las que cada persona es hija, ya resueltas. */
  private readonly familiasHijo = new Map<string, Nucleo[]>();
  /** Familias en las que cada persona es progenitora. */
  private readonly familiasProgenitor = new Map<string, Nucleo[]>();

  constructor(
    ix: Indice,
    fila: Map<string, number>,
    xIni: Map<string, number>,
    ordenPorFila: string[][],
    bloquesPorFila: string[][][] = [],
    separacionesPorFila: number[][] = [],
  ) {
    this.bloquesPorFila = bloquesPorFila;
    this.separaciones = separacionesPorFila;
    for (const [f, handles] of ordenPorFila.entries()) {
      this.ordenFilas[f] = [...handles];
      for (const h of handles) {
        this.nodos.set(h, {
          handle: h,
          fila: f,
          x: xIni.get(h) ?? 0,
          y: f * ALTO_FILA,
          vx: 0,
          fijado: false,
        });
      }
    }

    for (const fam of ix.arbol.familias.values()) {
      const progenitores = [fam.padre, fam.madre].filter(
        (p): p is string => !!p && this.nodos.has(p),
      );
      const hijos = fam.hijos
        .map((r) => r.hlink)
        .filter((h) => this.nodos.has(h));
      if (!progenitores.length && !hijos.length) continue;

      const n: Nucleo = { progenitores, hijos };
      if (progenitores.length === 2) {
        const [a, b] = progenitores as [string, string];
        if (this.nodos.get(a)?.fila === this.nodos.get(b)?.fila) n.pareja = [a, b];
      }
      this.nucleos.push(n);
      if (hijos.length > 1) this.gruposHermanos.push(hijos);

      for (const h of hijos) {
        const ls = this.familiasHijo.get(h);
        if (ls) ls.push(n);
        else this.familiasHijo.set(h, [n]);
      }
      for (const p of progenitores) {
        const ls = this.familiasProgenitor.get(p);
        if (ls) ls.push(n);
        else this.familiasProgenitor.set(p, [n]);
      }
    }
  }

  /** Posicion vertical de una fila. */
  static yDeFila(f: number): number {
    return f * ALTO_FILA;
  }

  /**
   * Un paso de simulacion. Devuelve la energia que queda, para saber cuando
   * dejar de simular y no gastar cuadros de balde.
   */
  paso(dt = 1): number {
    // Se guarda la posicion de partida porque la velocidad final se deduce
    // del desplazamiento REAL, no de la fuerza aplicada. Ver mas abajo.
    const anterior = new Map<string, number>();
    for (const n of this.nodos.values()) anterior.set(n.handle, n.x);

    const f = new Map<string, number>();
    const suma = (h: string, v: number): void => {
      f.set(h, (f.get(h) ?? 0) + v);
    };
    const x = (h: string): number => this.nodos.get(h)?.x ?? 0;

    // --- parejas: resorte corto y fuerte ---------------------------------
    for (const n of this.nucleos) {
      if (!n.pareja) continue;
      const [a, b] = n.pareja;
      const d = x(b) - x(a);
      const objetivo = Math.sign(d || 1) * separacionMinima("pareja");
      const error = d - objetivo;
      suma(a, error * K_PAREJA);
      suma(b, -error * K_PAREJA);
    }

    // --- vecinos de fila: resorte con longitud de reposo ------------------
    // Sin esto el diagrama se ensancha sin tope. La separacion de mas abajo
    // solo pone un MINIMO, y todas las demas fuerzas colocan a cada uno
    // respecto a su familia sin que nada tire hacia dentro: dos padres cuyos
    // hijos estan lejos se separan, sus propios padres los siguen, y la cosa
    // crece sola. Este resorte da el unico termino de contraccion que hay, y
    // es lo que mantiene el conjunto compacto.
    this.ordenFilas.forEach((fila, f) => {
      if (!fila) return;
      const sep = this.separaciones[f] ?? [];
      for (let i = 1; i < fila.length; i++) {
        const a = fila[i - 1] as string;
        const b = fila[i] as string;
        const error = x(b) - x(a) - (sep[i - 1] ?? separacionMinima("ajenos"));
        suma(a, error * K_VECINOS);
        suma(b, -error * K_VECINOS);
      }
    });

    // --- hermanos: cohesion suave hacia su propio centro ------------------
    for (const g of this.gruposHermanos) {
      const c = media(g.map(x));
      for (const h of g) suma(h, (c - x(h)) * K_HERMANOS);
    }

    // --- hijos colgando del punto medio de los progenitores --------------
    for (const n of this.nucleos) {
      if (!n.progenitores.length || !n.hijos.length) continue;
      const medioPadres = media(n.progenitores.map(x));
      const medioHijos = media(n.hijos.map(x));
      for (const h of n.hijos) suma(h, (medioPadres - medioHijos) * K_HIJOS);
      for (const p of n.progenitores) suma(p, (medioHijos - medioPadres) * K_PADRES);
    }

    // --- integracion (solo en X: no hay componente vertical) -------------
    for (const nodo of this.nodos.values()) {
      if (nodo.fijado) {
        nodo.vx = 0;
        continue;
      }
      let v = (nodo.vx + (f.get(nodo.handle) ?? 0) * dt) * AMORTIGUACION;
      if (v > VEL_MAX) v = VEL_MAX;
      else if (v < -VEL_MAX) v = -VEL_MAX;
      nodo.x += v * dt;
    }

    this.separar();

    // La velocidad sale del desplazamiento que de verdad ocurrio, no de la
    // fuerza. Esto es lo que hace que el sistema se asiente: un nodo que la
    // separacion deja bloqueado contra su vecino termina con velocidad casi
    // cero, en vez de seguir acumulando la fuerza del resorte y quedarse
    // oscilando para siempre contra la restriccion.
    let energia = 0;
    let sumaVel = 0;
    let libres = 0;
    for (const nodo of this.nodos.values()) {
      if (nodo.fijado) {
        nodo.vx = 0;
        continue;
      }
      nodo.vx = (nodo.x - (anterior.get(nodo.handle) ?? nodo.x)) / dt;
      libres++;
      sumaVel += nodo.vx;
    }

    // Todas las fuerzas de aqui son de atraccion, asi que la suma de fuerzas
    // internas es cero y "mover el conjunto entero" es un modo libre del
    // sistema: en cuanto coge velocidad, nada la frena. El barrido de
    // separacion, que solo empuja hacia la derecha, se lo regala en el primer
    // paso, y el diagrama se va a la deriva sin asentarse nunca.
    //
    // Se elimina quitando la velocidad media a todo el mundo. El movimiento
    // relativo, que es el que coloca a cada uno en su sitio, no se toca.
    const deriva = libres ? sumaVel / libres : 0;
    for (const nodo of this.nodos.values()) {
      if (nodo.fijado) continue;
      nodo.vx -= deriva;
      energia += Math.abs(nodo.vx);
    }

    return this.nodos.size ? energia / this.nodos.size : 0;
  }

  /**
   * Separacion minima entre vecinos de la misma fila.
   *
   * Se resuelve moviendo posiciones, no aplicando fuerzas: con fuerzas el
   * sistema se queda vibrando alrededor del contacto. Y se recorre en el
   * orden de la fila, asi que el orden nunca se invierte.
   */
  private separar(): void {
    this.ordenFilas.forEach((fila, f) => {
      if (!fila || fila.length < 2) return;
      const sep = this.separaciones[f] ?? [];
      const nodos: NodoFisica[] = [];
      for (const h of fila) {
        const n = this.nodos.get(h);
        if (n) nodos.push(n);
      }
      if (nodos.length < 2) return;

      const antes = nodos.map((n) => n.x);

      // Un unico barrido de izquierda a derecha satisface TODAS las
      // restricciones de la fila. Con iteraciones parciales no basta: un
      // apreton en un extremo tarda tantas iteraciones como nodos tenga la
      // fila en llegar al otro, y con 35 personas en una fila eso dejaba
      // decenas de pares solapados.
      for (let i = 1; i < nodos.length; i++) {
        const a = nodos[i - 1] as NodoFisica;
        const b = nodos[i] as NodoFisica;
        const minimo = a.x + (sep[i - 1] ?? separacionMinima("ajenos"));
        if (b.x < minimo) b.x = minimo;
      }

      // El barrido solo empuja hacia la derecha, asi que la fila entera se
      // iria desviando. Se corrige con un desplazamiento uniforme, que no
      // puede romper ninguna restriccion porque mueve a todos lo mismo.
      //
      // Si alguien esta sujeto (lo esta arrastrando quien mira), el
      // desplazamiento es el que devuelve a esa persona a su sitio exacto.
      const sujeto = nodos.findIndex((n) => n.fijado);
      let corrimiento: number;
      if (sujeto >= 0) {
        corrimiento = (antes[sujeto] as number) - (nodos[sujeto] as NodoFisica).x;
      } else {
        let s = 0;
        for (let i = 0; i < nodos.length; i++) {
          s += (antes[i] as number) - (nodos[i] as NodoFisica).x;
        }
        corrimiento = s / nodos.length;
      }
      if (corrimiento !== 0) {
        for (const n of nodos) {
          n.x += corrimiento;
        }
      }
    });
  }

  /**
   * Simula hasta que se asiente, o hasta `maxPasos`.
   *
   * Se llama UNA vez al cargar y se guarda el resultado. Durante el pan y el
   * zoom no se simula nada: las posiciones ya estan calculadas.
   */
  asentar(
    maxPasos = 4000,
    maxMs = 500,
  ): { pasos: number; energia: number; motivo: "reposo" | "pasos" | "tiempo" } {
    const t0 = Date.now();
    let energia = Number.POSITIVE_INFINITY;
    let i = 0;
    let motivo: "reposo" | "pasos" | "tiempo" = "pasos";
    for (; i < maxPasos; i++) {
      energia = this.paso();
      const l = this.limites();
      const umbral = Math.max(ENERGIA_MINIMA, (l.x1 - l.x0) * ENERGIA_RELATIVA);
      if (energia < umbral) {
        motivo = "reposo";
        break;
      }
      // Tope de tiempo: el calculo es de una sola vez, pero no puede dejar la
      // aplicacion colgada un rato largo en un arbol enorme.
      if ((i & 31) === 31 && Date.now() - t0 > maxMs) {
        motivo = "tiempo";
        break;
      }
    }
    return { pasos: i + 1, energia, motivo };
  }

  /** Sujeta a alguien en una posicion (arrastre). */
  fijar(handle: string, x: number): void {
    const n = this.nodos.get(handle);
    if (!n) return;
    n.x = x;
    n.vx = 0;
    n.fijado = true;
  }

  /** Lo suelta: a partir de aqui la amortiguacion lo acomoda. */
  liberar(handle: string): void {
    const n = this.nodos.get(handle);
    if (n) n.fijado = false;
  }

  /** Rectangulo que ocupa todo el conjunto, para encuadrar. */
  limites(): { x0: number; y0: number; x1: number; y1: number } {
    let x0 = Number.POSITIVE_INFINITY;
    let x1 = Number.NEGATIVE_INFINITY;
    let y0 = Number.POSITIVE_INFINITY;
    let y1 = Number.NEGATIVE_INFINITY;
    for (const n of this.nodos.values()) {
      if (n.x < x0) x0 = n.x;
      if (n.x > x1) x1 = n.x;
      if (n.y < y0) y0 = n.y;
      if (n.y > y1) y1 = n.y;
    }
    if (!Number.isFinite(x0)) return { x0: 0, y0: 0, x1: 0, y1: 0 };
    return { x0, y0, x1, y1 };
  }
}

function media(xs: number[]): number {
  if (!xs.length) return 0;
  let s = 0;
  for (const x of xs) s += x;
  return s / xs.length;
}
